"use client"
import React from 'react';
import styled from 'styled-components';
import { MockBrowser, BrowserContent } from './primitives'

const SIG_ITEMS = [
  { ticker: 'NVDA', score: 87, dir: 'LONG'  },
  { ticker: 'MSFT', score: 76, dir: 'LONG'  },
  { ticker: 'INTC', score: 23, dir: 'SHORT' },
];

const SIG_FACTORS = [
  { name: 'Momentum', score: 82 },
  { name: 'Quality',  score: 91 },
  { name: 'Growth',   score: 88 },
  { name: 'Value',    score: 41 },
];

const BACKTEST_STATS = [
  { label: 'Sharpe', value: '1.82' },
  { label: 'Max DD', value: '-12.3%' },
  { label: 'Win Rate', value: '68%' },
  { label: 'Ann. Return', value: '+24.6%' },
];

const EXPOSURE_DATA = [
  { sector: 'Technology', pct: 42, long: true },
  { sector: 'Financials', pct: 8,  long: true },
  { sector: 'Healthcare', pct: 0,  long: true },
  { sector: 'Energy',     pct: -5, long: false },
];

// ---- existing styled components (unchanged) ----
const SigVHeader = styled.div`
  margin-bottom: 14px;
`;
const SigVTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111;
  margin-bottom: 8px;
`;
const SigPipeline = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const SigPipeStep = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${({ $active }) => ($active ? '#fff' : '#bbb')};
  background: ${({ $active }) => ($active ? '#111' : 'transparent')};
  padding: ${({ $active }) => ($active ? '2px 7px' : '0')};
  border-radius: 3px;
`;
const SigPipeArrow = styled.span`
  font-size: 9px;
  color: #ddd;
`;
const SigRows = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
`;
const SigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-bottom: 1px solid #f5f5f5;

  &:last-child { border-bottom: none; }

  @media (max-width: 968px) {
    padding: 8px 10px;
    gap: 8px;
  }
`;
const SigDir = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ $long }) => ($long ? '#16a34a' : '#dc2626')};
  width: 52px;
  flex-shrink: 0;
`;
const SigTicker = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #111;
  width: 38px;
  flex-shrink: 0;
`;
const SigBarTrack = styled.div`
  flex: 1;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
`;
const SigBarFill = styled.div`
  height: 100%;
  width: ${({ $w }) => $w}%;
  background: ${({ $long }) => ($long ? '#16a34a' : '#dc2626')};
  border-radius: 2px;
`;
const SigScore = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #111;
  width: 24px;
  text-align: right;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;
const FactorSection = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
`;
const FactorSectionTitle = styled.div`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.07em;
  color: #bbb;
  text-transform: uppercase;
  margin-bottom: 10px;
`;
const FactorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  &:last-child { margin-bottom: 0; }
`;
const FactorName = styled.div`
  font-size: 11px;
  color: #555;
  width: 70px;
  flex-shrink: 0;
`;
const FactorTrack = styled.div`
  flex: 1;
  height: 4px;
  background: #e4e4e4;
  border-radius: 2px;
  overflow: hidden;
`;
const FactorFill = styled.div`
  height: 100%;
  width: ${({ $w }) => $w}%;
  background: ${({ $w }) => ($w >= 70 ? '#16a34a' : $w >= 50 ? '#f59e0b' : '#dc2626')};
  border-radius: 2px;
`;
const FactorScore = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #333;
  width: 22px;
  text-align: right;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

// ---- new styled components for added sections ----
const BacktestSection = styled(FactorSection)``;
const BacktestChartWrap = styled.div`
  margin-bottom: 10px;
  svg { display: block; width: 100%; height: 44px; }
`;
const BacktestStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
`;
const BacktestStatItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
`;
const BacktestStatLabel = styled.span`
  color: #888;
`;
const BacktestStatValue = styled.span`
  font-weight: 700;
  color: #111;
`;
const ExposureSection = styled(FactorSection)``;
const ExposureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  &:last-child { margin-bottom: 0; }
`;
const ExposureSector = styled.div`
  font-size: 11px;
  color: #555;
  width: 70px;
  flex-shrink: 0;
`;
const ExposureTrack = styled.div`
  flex: 1;
  height: 4px;
  background: #e4e4e4;
  border-radius: 2px;
  overflow: hidden;
`;
const ExposureFill = styled.div`
  height: 100%;
  width: ${({ $w }) => $w}%;
  background: ${({ $long }) => ($long ? '#16a34a' : '#dc2626')};
  border-radius: 2px;
`;
const ExposurePct = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $long }) => ($long ? '#16a34a' : '#dc2626')};
  width: 32px;
  text-align: right;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
`;

export const SignalVisual = () => (
  <MockBrowser $path={'/product_images/2.webp'}>
    <BrowserContent>
      <SigVHeader>
        <SigVTitle>Signal Output</SigVTitle>
        <SigPipeline>
          {['RAW DATA', 'CLEAN', 'MODEL', 'SIGNAL'].map((s, i) => (
            <React.Fragment key={s}>
              <SigPipeStep $active={i === 3}>{s}</SigPipeStep>
              {i < 3 && <SigPipeArrow>→</SigPipeArrow>}
            </React.Fragment>
          ))}
        </SigPipeline>
      </SigVHeader>

      <SigRows>
        {SIG_ITEMS.map((sig) => (
          <SigRow key={sig.ticker}>
            <SigDir $long={(sig.dir === 'LONG')}>{sig.dir === 'LONG' ? '↑' : '↓'} {sig.dir}</SigDir>
            <SigTicker>{sig.ticker}</SigTicker>
            <SigBarTrack>
              <SigBarFill $w={sig.score} $long={sig.dir === 'LONG'} />
            </SigBarTrack>
            <SigScore>{sig.score}</SigScore>
          </SigRow>
        ))}
      </SigRows>

      <FactorSection>
        <FactorSectionTitle>Factor breakdown · NVDA</FactorSectionTitle>
        {SIG_FACTORS.map((f) => (
          <FactorRow key={f.name}>
            <FactorName>{f.name}</FactorName>
            <FactorTrack><FactorFill $w={f.score} /></FactorTrack>
            <FactorScore>{f.score}</FactorScore>
          </FactorRow>
        ))}
      </FactorSection>

      {/* ---- new: backtest performance ---- */}
      <BacktestSection>
        <FactorSectionTitle>Backtest · NVDA Long-Only</FactorSectionTitle>
        <BacktestChartWrap>
          <svg viewBox="0 0 200 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="btgrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a34a" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* fake cumulative return line */}
            <path d="M0,40 L30,32 L50,36 L80,24 L110,18 L140,14 L170,10 L200,8 L200,44 L0,44 Z" fill="url(#btgrad)" />
            <path d="M0,40 L30,32 L50,36 L80,24 L110,18 L140,14 L170,10 L200,8" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </BacktestChartWrap>
        <BacktestStatsGrid>
          {BACKTEST_STATS.map((stat) => (
            <BacktestStatItem key={stat.label}>
              <BacktestStatLabel>{stat.label}</BacktestStatLabel>
              <BacktestStatValue>{stat.value}</BacktestStatValue>
            </BacktestStatItem>
          ))}
        </BacktestStatsGrid>
      </BacktestSection>

      {/* ---- new: sector exposure ---- */}
      <ExposureSection>
        <FactorSectionTitle>Sector Exposure</FactorSectionTitle>
        {EXPOSURE_DATA.map((exp) => {
          const absPct = Math.abs(exp.pct);
          return (
            <ExposureRow key={exp.sector}>
              <ExposureSector>{exp.sector}</ExposureSector>
              <ExposureTrack>
                <ExposureFill $w={absPct} $long={exp.long} />
              </ExposureTrack>
              <ExposurePct $long={exp.long}>
                {exp.long ? '+' : '-'}{absPct}%
              </ExposurePct>
            </ExposureRow>
          );
        })}
      </ExposureSection>

    </BrowserContent>
  </MockBrowser>
);
